import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { CentPrecisionMoney } from '@commercetools/platform-sdk';
import messages from './messages';
import CartConfirmationModal from '../../../components/CartConfirmationModal';
import useSlaService, {
  SLA_PRODUCT_SOURCE,
} from '../../../hooks/useSlaService';
import {
  addLineItems,
  addPromoCode,
  removePromoCode,
} from '../../../slices/cart';
import {
  ABANDONED_CART_PROMO_CODES_REGULAR_EXPRESSION,
  CONFLICTING_PROMO_TOASTER_TYPE_TO_SHOW_SESSION_STORAGE_KEY,
  CONFLICTING_PROMOS_TOAST_TYPE_10OFF_REMOVED,
  ITEM_LIST_IDS_TEALIUM,
  NEWDAY_TPM_PROMO,
} from '../../../utils/common/constants';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';
import {
  setDiscountPriceTotal,
  setPriceTotal,
} from '../../../slices/bundlePdp';
import AddToCartSimplifiedView from '../../../components/pdpV2/AddToCartSimplifiedView';
import { BundlePdpInStore } from '../../../slices/bundlePdp/types';
import { PDPInStore } from '../../../slices/pdp/types';
import { BundleDrupalSettings, SelectedVariant } from './types';
import { CartInStore } from '../../../slices/cart/types';
import { useThunkDispatch } from '../../../hooks/useThunkDispatch/useThunkDispatch';

const AddToCartBundlePdpContainer = () => {
  const { aovs, product: productFromDrupalSettings } =
    useDrupalSettings() as BundleDrupalSettings;
  const { publish } = drupalPubSubService;
  const [selectedVariants, setSelectedVariants] = useState<SelectedVariant[]>(
    [],
  );
  const [selectedPrimarySku, setSelectedPrimarySku] = useState('');
  const [total, setTotal] = useState<{
    currencyCode?: string;
    centAmount: number;
    fractionDigits?: number;
    type?: string;
  }>();
  const [isCartConfirmationModalOpen, setIsCartConfirmationModalOpen] =
    useState(false);
  const [totalDiscount, setTotalDiscount] = useState<{
    currencyCode?: string;
    centAmount: number;
    fractionDigits?: number;
    type?: string;
  }>();

  const dispatch = useThunkDispatch();

  const hasSelectedVariants = selectedVariants?.length > 0;
  const { selectedVariantsForBundleItemProducts } = useSelector(
    (state: { bundlePdp: BundlePdpInStore }) => state.bundlePdp,
  );
  const { selectedBedFrameAOVSKUs } = useSelector(
    (state: { pdp: PDPInStore }) => state.pdp,
  );
  const selectedBedFrameAOVSKUsString = selectedBedFrameAOVSKUs?.toString();

  const selectedVariantsForBundleItemProductsAsJSON = JSON.stringify(
    selectedVariantsForBundleItemProducts,
  );

  const getMatchingVariant = () =>
    Object.values(selectedVariantsForBundleItemProducts)?.[0][0]?.sku ===
    selectedPrimarySku
      ? Object.values(selectedVariantsForBundleItemProducts)?.[0][0]
      : null;

  useEffect(() => {
    const newSelectedVariants = Object.entries(
      selectedVariantsForBundleItemProducts,
    )
      .map(([, selectedVariantsForBundleItem], index) => {
        if (index === 0) {
          setSelectedPrimarySku(selectedVariantsForBundleItem?.[0]?.sku);
        }
        return selectedVariantsForBundleItem;
      })
      .flat();
    setSelectedVariants(newSelectedVariants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Used this instead of the original variable to avoid infinite loop in jest tests
    selectedVariantsForBundleItemProductsAsJSON,
  ]);

  const selectedBedFrameAOVVariants = useMemo(() => {
    if (!selectedBedFrameAOVSKUs?.length || aovs?.length <= 0) {
      return [];
    }

    const bedFrameAOVForSelectedSKU = aovs?.find(
      ({ targetSku }: { targetSku: string }) =>
        targetSku === selectedPrimarySku,
    )?.frames;

    if (!bedFrameAOVForSelectedSKU) {
      return [];
    }

    const bedFrameAOVVariantsForSelectedSKU =
      bedFrameAOVForSelectedSKU?.options
        ?.map((option) =>
          option?.variants?.map((variant) => ({
            ...variant,
            productKey: option?.productKey,
          })),
        )
        .flat() || null;

    const bedFrameAOVAltProductsVariantsForSelectedSKU =
      bedFrameAOVForSelectedSKU?.options
        ?.map((option) =>
          option?.alternativeProduct?.product?.variants?.map((variant) => ({
            ...variant,
            productKey: option?.alternativeProduct?.product?.productKey,
          })),
        )
        .filter(Boolean)
        .flat() || null;

    const allVariants = [
      ...(bedFrameAOVVariantsForSelectedSKU || []),
      ...(bedFrameAOVAltProductsVariantsForSelectedSKU || []),
    ];

    return allVariants?.filter(
      (variant) =>
        variant?.sku && selectedBedFrameAOVSKUs?.includes(variant.sku),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aovs, selectedBedFrameAOVSKUsString, selectedPrimarySku]);

  useEffect(() => {
    let [newTotal, newTotalDiscount] = selectedVariants.reduce(
      ([currentTotal, currentDiscount], selectedVariant) => [
        currentTotal + (selectedVariant?.price?.centAmount || 0),
        currentDiscount + (selectedVariant?.discount?.centAmount || 0),
      ],
      [0, 0],
    );

    if (selectedBedFrameAOVVariants?.length > 0) {
      selectedBedFrameAOVVariants.forEach((variant) => {
        newTotal += variant?.price?.centAmount || 0;

        newTotalDiscount += variant?.discount?.centAmount || 0;
      });
    }

    if (selectedVariants?.[0]?.price && newTotal) {
      setTotal({
        ...selectedVariants?.[0]?.price,
        centAmount: newTotal,
      });
      dispatch(
        setPriceTotal({
          centAmount: newTotal,
        }),
      );
      publish({
        subscription: 'onPDPTotalChange',
        parameters: { newTotal },
      });
      setTotalDiscount({
        ...selectedVariants?.[0]?.price,
        centAmount: newTotalDiscount,
      });
      dispatch(
        setDiscountPriceTotal({
          centAmount: newTotalDiscount,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBedFrameAOVSKUsString, selectedVariants]);

  const slaInput = useMemo(
    () => selectedVariants.filter(Boolean).map((variant) => variant.sku),
    [selectedVariants],
  );

  // Call SLA Service, so PDP SLA Messaging can be updated in Redux store.
  // Needed for Drupal Dynamic SLA in Worry-Free Guarantees component.
  useSlaService(
    slaInput,
    selectedVariants.length > 1,
    SLA_PRODUCT_SOURCE.SLA_PRODUCT_SOURCE_BUNDLE,
  );

  const { isLoading, addLineItemsError } = useSelector(
    (state: { cart: CartInStore }) => ({
      addLineItemsError: state.cart.addLineItemsError,
      isLoading: state.cart.status === 'loading',
    }),
  );

  const abandonedCartDiscountsApplied = useSelector(
    (state: { cart: CartInStore }) => {
      const discounts = state.cart.lineItems
        ?.filter((lineItem) => lineItem.custom?.fields?.discounts_json)
        .flatMap((lineItem) => {
          const discountsJson = JSON.parse(
            lineItem.custom?.fields?.discounts_json as string,
          ) as {
            method: string;
            coupon_code: string;
          }[];
          return discountsJson.filter(
            (discount) => discount.method === 'coupon',
          );
        })
        .map((discountJson) => discountJson.coupon_code)
        .filter((discount) => discount !== NEWDAY_TPM_PROMO)
        .filter((discount) =>
          ABANDONED_CART_PROMO_CODES_REGULAR_EXPRESSION.test(discount),
        );

      return [...new Set(discounts)];
    },
  );

  const toggleCartConfirmationModalModal = useCallback(
    (e?: MouseEvent) => {
      if (e) {
        e.preventDefault();
      }
      setIsCartConfirmationModalOpen((prevState) => !prevState);
    },
    [setIsCartConfirmationModalOpen],
  );

  const handleAddToCart = useCallback(async () => {
    let lineItems = Object.entries(
      selectedVariants.reduce(
        (acc: Record<string, number>, curr) => ({
          ...acc,
          [curr.sku]: acc[curr.sku] ? acc[curr.sku] + 1 : 1,
        }),
        {},
      ),
    ).map(([currentSKU, currentQuantity]) => ({
      sku: currentSKU,
      quantity: currentQuantity,
      item_list_id: ITEM_LIST_IDS_TEALIUM.bundleDetailPage,
    }));

    if (
      selectedBedFrameAOVVariants.length > 0 &&
      selectedBedFrameAOVSKUs?.length > 0
    ) {
      lineItems = [
        ...lineItems,
        ...(selectedBedFrameAOVSKUs?.map((sku) => ({
          sku,
          quantity: 1,
          item_list_id: ITEM_LIST_IDS_TEALIUM.productDetailPageAOV,
        })) ?? []),
      ];
    }

    try {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await dispatch(addLineItems({ lineItems }));
      const matchingVariant = getMatchingVariant();
      if (matchingVariant?.isProductTest) {
        if (abandonedCartDiscountsApplied?.length) {
          // Using classic for because we need to wait until each promise is resolved (to get the new version of the cart)
          // eslint-disable-next-line no-restricted-syntax
          for (const abandonedCartDiscount of abandonedCartDiscountsApplied) {
            // eslint-disable-next-line no-await-in-loop, @typescript-eslint/await-thenable
            await dispatch(removePromoCode(abandonedCartDiscount));
          }
          sessionStorage.setItem(
            CONFLICTING_PROMO_TOASTER_TYPE_TO_SHOW_SESSION_STORAGE_KEY,
            CONFLICTING_PROMOS_TOAST_TYPE_10OFF_REMOVED,
          );
        }
        dispatch(addPromoCode(NEWDAY_TPM_PROMO));
        localStorage.setItem('product_upgrade_test', selectedPrimarySku);
      }
      toggleCartConfirmationModalModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding line items to cart:', error);
      // Error handling is done in the slice
    }
    // needed for getMatchingVariant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    abandonedCartDiscountsApplied,
    dispatch,
    selectedBedFrameAOVSKUs,
    selectedPrimarySku,
    selectedVariants,
    toggleCartConfirmationModalModal,
  ]);

  const { formatMessage } = useIntl();

  return (
    <>
      <AddToCartSimplifiedView
        addToCartButtonTitle={formatMessage(messages.addToCartMultipleItems)}
        selectedSku=''
        hasAddLineItemsError={addLineItemsError ?? false}
        handleAddToCart={() => {
          handleAddToCart();
        }}
        isAddToCartButtonLoading={isLoading}
        showTotals={hasSelectedVariants}
        total={total as CentPrecisionMoney}
        totalDiscount={totalDiscount as CentPrecisionMoney}
        productTitle={productFromDrupalSettings?.title}
      />
      {isCartConfirmationModalOpen && (
        <CartConfirmationModal
          data-testid='cart-confirmation-modal'
          onCloseModal={toggleCartConfirmationModalModal}
        />
      )}
    </>
  );
};
export default AddToCartBundlePdpContainer;
